import { Stack } from "@mui/material";
import {
  AddedItem,
  CategoryTitle,
  DiffGroupWrapper,
  DiffRow,
  DiffValue,
  NumericDiff,
  RemovedItem,
} from "components/search/input/DifferencePanel/common";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  DiffNameBasedMaterialChemistry,
  NameBasedMaterialChemistry,
} from "store/api/generatedApi";

interface PercentDiffProps {
  previous: number | null | undefined;
  updated: number | null | undefined;
  format: (val: number) => string;
}

const PercentDiff = ({ previous, updated, format }: PercentDiffProps) => {
  if (previous == null && updated == null) return <DiffValue>—</DiffValue>;
  if (previous == null && updated != null) {
    return <DiffValue color="success.main">+ {format(updated)}%</DiffValue>;
  }
  if (updated == null && previous != null) {
    return <DiffValue color="error.main">- {format(previous)}%</DiffValue>;
  }
  if (previous === updated) {
    return <DiffValue>{format(updated ?? 0)}%</DiffValue>;
  }
  return (
    <NumericDiff
      previous={previous ?? 0}
      updated={updated ?? 0}
      suffix="%"
      format={format}
    />
  );
};

interface Props {
  diff: DiffNameBasedMaterialChemistry;
  base: NameBasedMaterialChemistry | null;
}

export const MaterialChemistryDiffItem = ({ diff, base }: Props) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const formatNumber4DP = formatNumber(4, true);

  const { previous, updated, key } = diff;
  const isAdded = previous === null;
  const isRemoved = updated === null;

  const effectivePrevious = isAdded ? base : previous;
  const effectiveUpdated = isRemoved ? base : updated;

  return (
    <DiffGroupWrapper>
      {isAdded ? (
        <AddedItem>{key}</AddedItem>
      ) : isRemoved ? (
        <RemovedItem>{key}</RemovedItem>
      ) : (
        <CategoryTitle>{key}</CategoryTitle>
      )}
      <Stack sx={{ pl: 2 }}>
        <DiffRow
          title={t("materialChemistryLoc")}
          value={
            <PercentDiff
              previous={effectivePrevious?.loc}
              updated={effectiveUpdated?.loc}
              format={formatNumber4DP}
            />
          }
        />
        <DiffRow
          title={t("materialChemistryScale")}
          value={
            <PercentDiff
              previous={effectivePrevious?.scale}
              updated={effectiveUpdated?.scale}
              format={formatNumber4DP}
            />
          }
        />
      </Stack>
    </DiffGroupWrapper>
  );
};
